import { useTranslatePortalCustomText } from "@babackapp/private-api-client/client/use-translate-portal-custom-text";
import { Button } from "@babackapp/ui/components/button";
import { LoaderIcon, WandSparkles } from "lucide-react";
import { useMemo, useState } from "react";

type FormValue = { lang: string; text: string };

type Props = {
  shopId: string;
  values: FormValue[];
  languages: string[];
  onTranslated: (translated: Record<string, string>) => void;
};

/**
 * Reuses the AI translation endpoint to fill every empty language from the
 * first filled one. Kept intentionally minimal compared to the return reason
 * variant — the wording drawer already shows the source text inline, so we
 * don't need an interstitial dialog to pick the source language.
 */
export function TranslatePortalCustomTextButton({
  shopId,
  values,
  languages,
  onTranslated,
}: Props) {
  const { mutateAsync } = useTranslatePortalCustomText();
  const [isLoading, setIsLoading] = useState(false);

  const source = useMemo(() => {
    return values.find((v) => v.text.trim().length > 0) ?? null;
  }, [values]);

  const targets = useMemo(
    () =>
      languages.filter(
        (lang) => lang !== source?.lang && !valueFor(values, lang)?.trim(),
      ),
    [languages, source, values],
  );

  const canTranslate =
    source != null && targets.length > 0 && !isLoading && languages.length > 1;

  const onClick = async () => {
    if (!canTranslate || !source) return;
    setIsLoading(true);
    try {
      const { translated } = await mutateAsync({
        shopId,
        text: source.text,
        sourceLocale: source.lang,
        targetLocales: targets,
      });
      onTranslated(translated);
    } finally {
      setIsLoading(false);
    }
  };

  if (languages.length < 2) return null;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={!canTranslate}
      onClick={onClick}
      className="gap-1.5"
    >
      {isLoading ? (
        <LoaderIcon className="size-3.5 animate-spin" />
      ) : (
        <WandSparkles className="size-3.5" />
      )}
      Traduire via IA
    </Button>
  );
}

function valueFor(values: FormValue[], lang: string): string | undefined {
  return values.find((v) => v.lang === lang)?.text;
}
